// WHEN IS A READ DONE WITH, for a region that reveals once (design-system §5).
//
// A screen whose layout is decided by several reads holds its region behind
// one reserving skeleton until every read it paints has finished, then
// reveals it in one commit. "Finished" is answered, failed, or held back
// because the browser is offline: a failed or paused read still opens the
// region (fail-closed absence), and its own panel then says so in one line,
// never a skeleton that does not end. A read the region never asked for (a
// panel the reader is refused, a disabled query) counts as finished.
//
// `readSettled` is pure, so the peek, the customer 360 and any later region
// share one rule. `useReadsHeldOffline` answers whether the browser is
// offline, for a gate that waits on a read `readSettled` cannot see (the
// member directory, whose hook reports a paused read as pending).
//
// THE REVEAL LATCHES (`useRevealLatch`). A read that settled can fall back to
// pending: react-query asks a failed read that holds no data again as pending
// with no error (a window regaining focus, the network returning), and a
// paused read starts fetching again the moment the browser is back online. A
// gate read straight from the reads would then take the painted region back
// to its skeleton, and an open dialog inside it with it. So a region reveals
// once, as `ViewsRegion` does for the books, and a later read renders inside
// its own panel's states.

import { useState, useSyncExternalStore } from "react";
import { onlineManager } from "@tanstack/react-query";

export interface ReadLikeState {
  readonly data?: unknown;
  readonly error?: unknown;
  readonly fetchStatus?: string;
  readonly isPending?: boolean;
}

export function readSettled(read: ReadLikeState, asked = true): boolean {
  return (
    !asked ||
    read.data !== undefined ||
    (read.error !== null && read.error !== undefined) ||
    read.fetchStatus === "paused" ||
    read.isPending === false
  );
}

function subscribeOnline(onChange: () => void): () => void {
  return onlineManager.subscribe(onChange);
}

function browserOffline(): boolean {
  return !onlineManager.isOnline();
}

/** Is react-query holding its reads because the browser has no network? A
 * read it holds reports pending until the network returns, so a gate that
 * waits on `isPending` alone waits for the whole cut (design-system §3.9's
 * `OfflineState`). The board, the deal list, the forecast, the customer 360,
 * the side peek and the task list ask it before they wait on an admin's
 * member directory. */
export function useReadsHeldOffline(): boolean {
  return useSyncExternalStore(subscribeOnline, browserOffline, () => false);
}

/** The props that make a person's `RefLabel` ask the names read (D-441),
 * spread onto the label: `{ kind: "user" }`, or nothing.
 *
 * The member directory is an admin read, so for everyone else it holds no
 * names, and a person used to read as the short-id chip, the "019e38d4" the
 * guide's quote screenshots showed. `GET /v1/refs` names a co-member for any
 * reader with identity.view_member_names. It is not asked while the
 * directory is still answering (its resolving mark says that already), and
 * not while the browser is offline, where it cannot answer and the short id
 * is the honest state at once (#293). */
export function useMemberRefAsk(directoryPending: boolean): { readonly kind?: "user" } {
  const offline = useReadsHeldOffline();
  return directoryPending || offline ? {} : { kind: "user" };
}

/** Has this region been revealed (design-system §5)? True from the first
 * render `settled` holds, for as long as `key` stays the same, so a read
 * asked again after the reveal never brings the skeleton back. A new `key`
 * (another record shown in the same mounted panel) waits again. */
export function useRevealLatch(settled: boolean, key = ""): boolean {
  const [revealedFor, setRevealedFor] = useState<string | undefined>(settled ? key : undefined);
  if (settled && revealedFor !== key) setRevealedFor(key);
  return settled || revealedFor === key;
}
